import {
  EventType,
  InteractionRequiredAuthError,
  type AccountInfo,
  type AuthenticationResult,
  type EndSessionRequest,
  type EventMessage,
  type PublicClientApplication,
  type RedirectRequest,
  type SilentRequest,
} from '@azure/msal-browser'
import { MsalClient } from '@/lib/msal/msal-client'
import { buildLoginRequest } from '@/features/auth/lib/login-request'
import { useLoginRedirectStore } from './stores/login-redirect-store'

export class AuthService {
  private readonly client: PublicClientApplication
  private interactiveAcquirePromise: Promise<string> | null = null

  constructor(client: PublicClientApplication = MsalClient.getInstance()) {
    this.client = client
  }

  async initialize(): Promise<void> {
    await this.client.initialize().then(() => {
      this.client.addEventCallback((event: EventMessage) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const account = event.payload as AccountInfo
          this.client.setActiveAccount(account)
        }
      })
    })
  }

  loginRedirect(request = buildLoginRequest(), destination?: string): Promise<void> {
    this.persistRedirectDestination(destination)
    return this.client.loginRedirect(request)
  }

  async handleRedirectPromise(): Promise<AuthenticationResult | null> {
    const result = await this.client.handleRedirectPromise({ navigateToLoginRequestUrl: false })

    if (result?.account) {
      this.client.setActiveAccount(result.account)
    }

    return result
  }

  async acquireAccessToken(request?: SilentRequest): Promise<string> {
    const account =
      request?.account ??
      this.client.getActiveAccount() ??
      this.client.getAllAccounts()[0]

    if (!account) {
      throw new Error('No signed-in account. Sign in before calling the API.')
    }

    const tokenRequest: SilentRequest = {
      ...buildLoginRequest(),
      ...request,
      account,
    }

    try {
      const result = await this.client.acquireTokenSilent(tokenRequest)
      const token = result.accessToken

      if (!token) {
        throw new Error('No access token returned from MSAL.')
      }

      return token
    } catch (error) {
      if (!(error instanceof InteractionRequiredAuthError)) {
        throw error
      }

      return this.acquireTokenInteractively({
        ...tokenRequest,
        ...(error.claims ? { claims: error.claims } : {}),
      })
    }
  }

  logoutRedirect(request?: EndSessionRequest): Promise<void> {
    const account =
      request?.account ??
      this.client.getActiveAccount() ??
      this.client.getAllAccounts()[0]

    return this.client.logoutRedirect({
      ...request,
      account,
      postLogoutRedirectUri:
        request?.postLogoutRedirectUri ??
        import.meta.env.VITE_ENTRA_POST_LOGOUT_REDIRECT_URI?.trim() ??
        window.location.origin,
    })
  }

  private acquireTokenInteractively(request: RedirectRequest): Promise<string> {
    if (this.interactiveAcquirePromise) {
      return this.interactiveAcquirePromise
    }

    this.persistRedirectDestination(
      `${window.location.pathname}${window.location.search}${window.location.hash}`
    )

    this.interactiveAcquirePromise = this.client
      .acquireTokenRedirect(request)
      .then(
        () =>
          // Redirect has started; keep API callers pending until the page unloads.
          new Promise<string>(() => { })
      )
      .catch((error: unknown) => {
        this.interactiveAcquirePromise = null
        throw error
      })

    return this.interactiveAcquirePromise
  }

  private persistRedirectDestination(destination?: string): void {
    if (!destination || destination === '/login' || destination.startsWith('/login/')) {
      return
    }

    useLoginRedirectStore.getState().setRedirectDestination(destination)
  }
}

export const authService = new AuthService()
